import type {
  BarFillArgs,
  PaintCanvasArgs,
} from '@components-shared/src/main/waveform/paintCanvas';
import {
  barCoordinates,
  roundRect,
  TOP_WAVE_HEIGHT,
} from '@components-shared/src/main/waveform/paintCanvas';
import { BOTTOM_WAVE_HEIGHT } from './constants';

export function paintCanvas(args: PaintCanvasArgs) {
  const {
    canvasRef,
    normalizedPeaks,
    maxBarHeight,
    barWidth,
    barMargin,
    playingBarNum,
    hoverXCoord,
    isActiveTrack,
    unPlayedColor = 'hsla(214, 8%, 84%, 1)',
    playedColor = 'hsla(197, 53%, 50%, 1)',
    showBottomHalf = true,
    topHalfHeight,
    bottomHalfHeight,
    gapHeight = 2,
    bottomHalfColor,
    hoverColor,
  } = args;

  const ref = canvasRef.current;
  const ctx = ref?.getContext('2d');
  if (!ref || !ctx) {
    return;
  }

  // erase the canvas before painting or waveforms will stack every update
  const bgColor = '#F3F4F6';
  const topWave = topHalfHeight ?? TOP_WAVE_HEIGHT;
  const bottomWave = bottomHalfHeight ?? BOTTOM_WAVE_HEIGHT;
  ctx.fillStyle = bgColor;
  ctx.clearRect(0, 0, ref.width, ref.height);

  const isHovering = !!hoverXCoord;

  ctx.beginPath();
  normalizedPeaks.forEach((p, i) => {
    const coord = barCoordinates({
      index: i,
      barWidth,
      barMargin,
      maxBarHeight,
      amplitude: p,
      containerHeight: ref.height,
      topWaveHeight: topHalfHeight,
    });

    // paint top half of waveform
    roundRect({
      ctx,
      x: coord.x,
      y: coord.y,
      w: coord.w,
      h: coord.h * topWave,
      r: 1.5,
    });

    const withinHover = hoverXCoord >= coord.x;
    const alreadyPlayed = i < playingBarNum;

    fillBar({
      ctx,
      barWidth,
      maxBarHeight,
      unPlayedColor,
      playedColor,
      opacity: 1,
      isHovering,
      withinHover,
      alreadyPlayed,
      isActiveTrack,
      isTopWave: true,
      hoverColor,
    });
  });
  ctx.closePath();

  ctx.beginPath();
  normalizedPeaks.forEach((p, i) => {
    const coord = barCoordinates({
      index: i,
      barWidth,
      barMargin,
      maxBarHeight,
      amplitude: p,
      containerHeight: ref.height,
      topWaveHeight: topHalfHeight,
    });

    // paint bottom half of waveform (except for in song page and preview song page)
    if (showBottomHalf) {
      roundRect({
        ctx,
        x: coord.x,
        y: coord.y + coord.h * topWave + gapHeight, // 2 = 2px space between top and bottom wave
        w: coord.w,
        h: coord.h * bottomWave,
        r: 1.5,
      });
    }

    const withinHover = hoverXCoord >= coord.x;
    const alreadyPlayed = i < playingBarNum;

    fillBar({
      ctx,
      barWidth,
      maxBarHeight,
      unPlayedColor,
      playedColor,
      opacity: 0.5,
      isHovering,
      withinHover,
      alreadyPlayed,
      isActiveTrack,
      isTopWave: false,
      bottomHalfColor,
    });
  });
  ctx.closePath();
}

function fillBar(args: BarFillArgs) {
  const {
    ctx,
    unPlayedColor,
    playedColor,
    opacity,
    isHovering,
    withinHover,
    alreadyPlayed,
    isActiveTrack,
    isTopWave,
    bottomHalfColor,
    hoverColor,
  } = args;

  function colorBar(color = playedColor): string {
    ctx.globalAlpha = opacity;
    return color;
  }

  // hover behind or in front of the current playing bar
  const isSeekingForward = withinHover && !alreadyPlayed;
  const isSeekingBack = !withinHover && alreadyPlayed;

  if (bottomHalfColor && !isTopWave) {
    ctx.fillStyle = colorBar(bottomHalfColor);
  } else if (!isHovering) {
    if (alreadyPlayed) {
      ctx.fillStyle = colorBar();
    } else {
      ctx.fillStyle = colorBar(unPlayedColor);
    }
  } else if (!isActiveTrack) {
    ctx.fillStyle = colorBar();
  } else if (isSeekingForward || isSeekingBack) {
    // seek color is a more transparent variation of played
    ctx.fillStyle = colorBar(hoverColor ?? hslToHsla(playedColor, 0.5));
  } else if (withinHover) {
    ctx.fillStyle = colorBar();
  } else {
    ctx.fillStyle = colorBar(unPlayedColor);
  }

  ctx.fill();
}

/**
 * convert hsl to hsla
 * @param hsl - hsl color string
 * @param alpha - alpha value
 * @returns - hsla color string
 */
function hslToHsla(hsl: string, alpha: number) {
  const [h, s, l] = hsl
    .replace(/hsla?\(/, '')
    .replace(/\)/, '')
    .split(',');
  return `hsla(${h}, ${s}, ${l}, ${alpha})`;
}
